/**
 * Scanning a receipt photo with a vision model running in LM Studio (Qwen-VL, Gemma 3, and
 * similar). The photo is shrunk in the browser and sent straight to your computer; nothing is
 * uploaded to split.milesnguyen.com. The result is a draft you check before adding.
 */
import {
  RECEIPT_PROMPT,
  isHeifHeader,
  parseReceiptsReply,
  receiptProgress,
  type ReceiptDraft,
} from "@split/core";
import { assistant } from "./assistant.svelte.ts";
import { chat, LlmError } from "./llm.ts";
import { aiQueue } from "./queue.svelte.ts";
import { SCAN_CONVERT_LIMIT_SECONDS } from "../../settings/receipts.ts";
import { APP_VERSION } from "../version.svelte.ts";

/** Writes one step of a scan to the scan log. */
type Note = (msg: string) => void;

const MAX_SIDE = 1600;

/** Shrinks a photo so a receipt stays readable but the request stays small. */
export async function photoToDataUrl(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser can't read the photo.");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return canvas.toDataURL("image/jpeg", 0.85);
}

/** Whether this browser can open the photo as it is (and the reason when it can't). */
async function opens(file: Blob): Promise<string> {
  try {
    const b = await createImageBitmap(file);
    const size = `${b.width}×${b.height}`;
    b.close?.();
    return size;
  } catch (e) {
    throw e instanceof Error ? e : new Error(String(e));
  }
}

const brand = (head: Uint8Array) =>
  head.length >= 12 && String.fromCharCode(...head.subarray(4, 8)) === "ftyp"
    ? String.fromCharCode(...head.subarray(8, 12))
    : [...head.subarray(0, 4)].map((b) => b.toString(16).padStart(2, "0")).join(" ");

/** A promise that gives up after a while instead of waiting forever. */
function within<T>(p: Promise<T>, seconds: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${what} took over ${seconds} seconds`)),
      seconds * 1000,
    );
  });
  return Promise.race([p, late]).finally(() => clearTimeout(timer));
}

/**
 * The photo in a form this browser can open: as it is when it can, and an iPhone HEIC photo
 * (which only Safari opens) converted to JPEG on this device. The converter is loaded only the
 * first time it's needed, so the app stays small.
 */
export async function readablePhoto(file: Blob, note: Note = () => {}): Promise<Blob> {
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  note(
    `Photo: ${(file as File).name ?? "(no name)"}, type "${file.type || "none"}", ${Math.round(file.size / 1024)} KB, starts with ${brand(head)}`,
  );
  try {
    note(`This browser opens it as it is (${await opens(file)})`);
    return file;
  } catch (e) {
    note(`This browser can't open it as it is: ${(e as Error).name}: ${(e as Error).message}`);
  }
  if (!isHeifHeader(head)) throw new Error(PHOTO_UNREADABLE);
  try {
    note("It's an iPhone photo (HEIC): loading the converter");
    const { heicTo } = await within(
      import("heic-to/csp"),
      SCAN_CONVERT_LIMIT_SECONDS,
      "Loading the converter",
    );
    note("Converting to JPEG");
    const jpeg = await within(
      heicTo({ blob: file, type: "image/jpeg", quality: 0.92 }),
      SCAN_CONVERT_LIMIT_SECONDS,
      "Converting the photo",
    );
    note(`Converted: ${Math.round(jpeg.size / 1024)} KB JPEG (${await opens(jpeg)})`);
    return jpeg;
  } catch (e) {
    note(
      `Converting failed: ${(e as Error)?.name ?? "Error"}: ${(e as Error)?.message ?? String(e)}`,
    );
    throw new Error(HEIC_UNREADABLE, { cause: e });
  }
}

const HEIC_UNREADABLE =
  "couldn't convert this iPhone photo (HEIC). Try a screenshot of it, or export it as a JPEG.";

const PHOTO_UNREADABLE =
  "this browser can't open that photo. Try a JPEG or PNG, or a screenshot of the receipt.";

const clock = () => {
  const d = new Date();
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, "0")}`;
};

/**
 * One receipt read from a photo, waiting for you to check and add it, kept until you add or
 * cancel it. A photo with two receipts gives two of these; picking three photos gives one or more
 * each. They're all checked and added the same way, one after another.
 */
export interface PendingScan {
  /** This receipt's place in the waiting list. */
  id: string;
  /** The group it was scanned in; it's only ever added there. */
  groupId: string;
  draft: ReceiptDraft;
  /** The photo, shrunk, to show beside the rows (empty when it didn't fit on this device). */
  photo: string;
  /** Which receipt in its photo this is (1, 2, …), and how many the photo had. */
  place: number;
  of: number;
  /** Pasted from another AI chat's reply instead of scanned here (there's no photo). */
  pasted?: boolean;
}

const PENDING_KEY = "scan:pending";

const isPending = (v: unknown): v is PendingScan =>
  !!v &&
  typeof (v as PendingScan).groupId === "string" &&
  !!(v as PendingScan).draft &&
  Array.isArray((v as PendingScan).draft.items);

function readPending(): PendingScan[] {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]") as unknown;
    // Before many receipts at once, one scan was kept on its own: it reads as a list of one.
    return (Array.isArray(v) ? v : [v]).filter(isPending).map((p) => ({
      ...p,
      id: p.id ?? crypto.randomUUID(),
      place: p.place ?? 1,
      of: p.of ?? 1,
    }));
  } catch {
    return [];
  }
}

class ReceiptScanner {
  /**
   * The receipts waiting for review, in the order they were read. They live here, not on the
   * Receipts screen, so leaving the page while the AI reads (or reloading) doesn't lose them;
   * they're saved on this device too.
   */
  pending = $state<PendingScan[]>(typeof localStorage === "undefined" ? [] : readPending());
  /** Which photo of the ones picked is being read (1-based), and how many were picked. */
  photo = $state({ n: 0, of: 0 });
  /** How many Receipts screens are open, to tell you when a scan finishes while none is. */
  screens = 0;
  busy = $state(false);
  /** Which step a scan is on, for the words next to the button. */
  stage = $state<"" | "opening" | "waiting" | "reading">("");
  error = $state("");
  thinking = $state("");
  /** Every step of the last scan, with the time (24-hour), also written to the console. */
  log = $state<string[]>([]);
  private abort: AbortController | null = null;
  private started = 0;

  /**
   * Adds every receipt read from one photo (or one paste) to the waiting list, in the order they
   * were read.
   */
  keep(groupId: string, drafts: ReceiptDraft[], photo: string, pasted = false) {
    this.pending = [
      ...this.pending,
      ...drafts.map((draft, i) => ({
        id: crypto.randomUUID(),
        groupId,
        draft,
        photo,
        place: i + 1,
        of: drafts.length,
        ...(pasted ? { pasted: true } : {}),
      })),
    ];
    this.save();
  }

  /** Forgets one receipt waiting for review (after adding it, or Cancel). */
  drop(id: string) {
    this.pending = this.pending.filter((p) => p.id !== id);
    this.save();
  }

  /** Forgets every receipt waiting for review in this group. */
  dropAll(groupId: string) {
    this.pending = this.pending.filter((p) => p.groupId !== groupId);
    this.save();
  }

  /** Saves the waiting list on this device: with the photos when there's room, else without. */
  private save() {
    const list = $state.snapshot(this.pending);
    try {
      if (!list.length) return localStorage.removeItem(PENDING_KEY);
      for (const [v, words] of [
        [list, "until they're added"],
        [list.map((p) => ({ ...p, photo: "" })), "without the photos, they didn't fit"],
      ] as const) {
        try {
          localStorage.setItem(PENDING_KEY, JSON.stringify(v));
          return this.note(`Kept ${list.length} waiting on this device (${words})`);
        } catch {
          /* too big with the photos: try without them */
        }
      }
    } catch {
      /* no storage on this device */
    }
    this.note("Couldn't keep the scans on this device; they stay until you reload");
  }

  /** Starts a fresh log for a new scan. */
  begin() {
    this.log = [];
    this.started = performance.now();
    this.note(`Scan started (app ${APP_VERSION}, ${navigator.userAgent})`);
  }

  note = (msg: string) => {
    const line = `${clock()} +${Math.round(performance.now() - this.started)} ms  ${msg}`;
    this.log = [...this.log, line];
    console.info("[scan]", line);
  };

  /** Shows an unexpected failure instead of stopping silently. */
  fail(e: unknown) {
    const err = e instanceof Error ? e : new Error(String(e));
    this.note(
      `Stopped by an error: ${err.name}: ${err.message}${err.stack ? `\n${err.stack}` : ""}`,
    );
    this.error = `The scan stopped: ${err.message}`;
    this.stage = "";
  }

  /** Set by Stop, so the photos still to read are left alone too. */
  stopped = false;

  stop() {
    this.stopped = true;
    this.abort?.abort();
  }

  /** The photo ready to show and scan (HEIC converted), or null with the reason in error. */
  async prepare(file: Blob): Promise<Blob | null> {
    this.error = "";
    this.stage = "opening";
    try {
      return await readablePhoto(file, this.note);
    } catch (e) {
      this.note(`Can't use the photo: ${(e as Error).message}`);
      this.error = `The scan stopped: ${(e as Error).message}`;
      return null;
    } finally {
      this.stage = "";
    }
  }

  /** Every receipt in the photo, in order (empty with the reason in error when none was read). */
  async scan(file: Blob): Promise<ReceiptDraft[]> {
    this.stage = "waiting";
    this.note("Waiting for its turn with the AI");
    try {
      return await aiQueue.first((id) => this.scanNow(file, id), "Receipt");
    } finally {
      this.stage = "";
    }
  }

  private async scanNow(file: Blob, job = ""): Promise<ReceiptDraft[]> {
    if (this.busy) {
      this.note("Another scan is still running, so this one stopped");
      this.error = "Another scan is still running. Wait for it or press Stop.";
      return [];
    }
    this.error = "";
    this.thinking = "";
    this.stage = "reading";
    this.note(
      `Checking the AI connection (${assistant.settings.baseUrl}, model ${assistant.settings.model || "not set"}): ${assistant.connection.status}`,
    );
    if (assistant.connection.status !== "ok") await assistant.check();
    if (assistant.connection.status !== "ok") {
      this.note(`AI not reachable: ${assistant.connection.status} ${assistant.connection.message}`);
      this.error =
        assistant.connection.message || "Can't reach LM Studio. Set it up on the Ask page first.";
      return [];
    }
    this.note("AI connected");
    this.busy = true;
    this.abort = new AbortController();
    try {
      const url = await photoToDataUrl(file);
      this.note(`Sending the photo (${Math.round((url.length * 3) / 4 / 1024)} KB) to the AI`);
      let first = true;
      const r = await chat(
        assistant.settings,
        [
          {
            role: "user",
            content: [
              { type: "text", text: RECEIPT_PROMPT },
              { type: "image_url", image_url: { url } },
            ],
          },
        ],
        [],
        (u) => {
          this.thinking = u.reasoning;
          if (first) {
            first = false;
            this.note("The AI started replying");
          }
          // "AI: Costco receipt 10/26 (5/44)" while the items stream in ("2 receipts (5/44)").
          const p = receiptProgress(u.content);
          const when = p.date
            ? ` ${Number(p.date.slice(5, 7))}/${Number(p.date.slice(8, 10))}`
            : "";
          aiQueue.report(
            job,
            p.receipts && p.receipts > 1
              ? `${p.receipts} receipts`
              : `${p.store ? `${p.store} receipt` : "Receipt"}${when}`,
            p.total ? p.done : undefined,
            p.total ?? undefined,
          );
          // Before the item count arrives (or while thinking), fill by how much has come in.
          if (!p.total) aiQueue.streamed(job, u.content.length + u.reasoning.length, 4000);
        },
        this.abort.signal,
      );
      this.note(`The AI finished: ${r.content.length} characters`);
      const all = parseReceiptsReply(r.content);
      if (!all.length) this.note(`No JSON in the reply. It starts: ${r.content.slice(0, 300)}`);
      all.forEach((d, i) =>
        this.note(
          `Receipt ${i + 1} of ${all.length}: ${d.store}, ${d.items.length} items, subtotal ${d.subtotalCents ?? "?"} cents, total ${d.totalCents ?? "?"} cents`,
        ),
      );
      // A receipt with no items isn't one to add (the model can list an empty one by mistake).
      const drafts = all.filter((d) => d.items.length);
      if (!drafts.length) {
        this.error =
          "The model didn't find any items. Try a sharper, flatter photo, or make sure the loaded model can read images (a vision model like Qwen-VL).";
      }
      return drafts;
    } catch (e) {
      this.note(`Stopped: ${(e as Error).name}: ${(e as Error).message}`);
      if ((e as Error).name !== "AbortError") {
        this.error =
          e instanceof LlmError ? e.message : `The scan stopped: ${(e as Error).message}`;
      }
      return [];
    } finally {
      this.busy = false;
      this.abort = null;
    }
  }
}

export const receiptScanner = new ReceiptScanner();
